import Big from 'big.js';
import { memo, useCallback } from 'react';
import { useFlashSentiment } from '../../hooks/useFlashSentiment';
import { SideEnum, type MarketDataSnapshotLevel, type Security } from '../../types';
import { Direction } from '../../types/Direction';
import { InlineFormattedNumber } from '../FormattedNumber';
import type { ClickRowCallback } from './Row';
import { Spread, SpreadRowWrapper, TopPrice } from './styles';

export const SpreadRow = memo(
  ({ spread, spreadBps, security, symbol, topBid, topOffer, onClickRow }: SpreadRowProps) => {
    const showPercent = Big(spreadBps ?? '0')
      .abs()
      .gt(1000);
    const relativeSpread = spreadBps
      ? showPercent
        ? Big(spreadBps).div(100).toPrecision(2)
        : Big(spreadBps).toPrecision(2)
      : undefined;

    const bidSentiment = useFlashSentiment(symbol, Direction.Asc, topBid?.Price);
    const offerSentiment = useFlashSentiment(symbol, Direction.Asc, topOffer?.Price);

    const handleClickBid = useCallback(
      () => onClickRow({ side: SideEnum.Buy, price: topBid?.Price ?? '' }),
      [onClickRow, topBid]
    );
    const handleClickOffer = useCallback(
      () => onClickRow({ side: SideEnum.Sell, price: topOffer?.Price ?? '' }),
      [onClickRow, topOffer]
    );

    return (
      <SpreadRowWrapper>
        <TopPrice onClick={handleClickBid}>
          <InlineFormattedNumber
            number={topBid?.Price}
            increment={security?.DefaultPriceIncrement}
            variant={bidSentiment}
          />
        </TopPrice>
        <Spread>
          <InlineFormattedNumber
            number={spread}
            increment={security?.DefaultPriceIncrement}
            currency={security?.QuoteCurrency}
          />
          <InlineFormattedNumber number={relativeSpread} increment="0.01" currency={showPercent ? '%' : 'BPS'} />
        </Spread>
        <TopPrice onClick={handleClickOffer}>
          <InlineFormattedNumber
            number={topOffer?.Price}
            increment={security?.DefaultPriceIncrement}
            variant={offerSentiment}
          />
        </TopPrice>
      </SpreadRowWrapper>
    );
  }
);

type SpreadRowProps = {
  spread?: string;
  spreadBps?: string;
  security?: Security;
  symbol: string;
  topBid?: MarketDataSnapshotLevel;
  topOffer?: MarketDataSnapshotLevel;
  onClickRow: ClickRowCallback;
};
